import { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, BarChart3, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatDate } from '@/utils/formatters';
import { TablePagination } from '@/components/ui/table-pagination';
import { TradingHistoryWithSource } from '@/services/prop-bot/prop-bot-trade-history-service';

interface PropMergedTradingHistoryTableProps {
  data: TradingHistoryWithSource | undefined;
  isLoading: boolean;
  onRefresh: () => void;
}

export function PropMergedTradingHistoryTable({
  data,
  isLoading,
  onRefresh
}: PropMergedTradingHistoryTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const trades = data?.trades || [];
  const source = data?.source;

  // Sort trades by timestamp in descending order (newest first)
  const sortedTrades = [...trades].sort((a, b) => {
    const dateA = new Date(a.timestamp.includes('T') ? a.timestamp : a.timestamp.replace(' ', 'T') + 'Z');
    const dateB = new Date(b.timestamp.includes('T') ? b.timestamp : b.timestamp.replace(' ', 'T') + 'Z');
    return dateB.getTime() - dateA.getTime();
  });

  // Paginate sorted trades
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTrades = sortedTrades.slice(startIndex, endIndex);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatAmount = (amount: number) => {
    return amount.toFixed(4);
  };

  if (isLoading) {
    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <div className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} bg-primary/20 rounded`} />
            </div>
            <div>
              <div className={`h-${isMobile ? "6" : "8"} w-48 bg-muted rounded`} />
              <div className={`h-4 w-64 bg-muted/60 rounded mt-1`} />
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-lg bg-muted`} />
        </div>
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`}>
        {/* Enhanced Header */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-blue-500/5 rounded-2xl blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3">
              <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
                <BarChart3 className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
              </div>
              <div>
                <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-foreground`}>
                  Trading History
                </h2>
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
                  View consolidated trading history from published analysis
                </p>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

        {/* No data message */}
        <Card className="overflow-hidden border hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-background/50">
          <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="p-4 bg-muted/50 rounded-full">
                <AlertTriangle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-medium">No Trading History Available</h3>
                <p className="text-muted-foreground text-sm max-w-md">
                  No trading data found. Please ensure that a bot analysis has been published in the Admin section for this bot or its linked bot.
                </p>
              </div>
              <Button variant="outline" onClick={onRefresh} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`}>
      {/* Enhanced Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-blue-500/5 rounded-2xl blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <BarChart3 className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
            </div>
            <div className="space-y-2">
              <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-foreground`}>
                Trading History
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
                  Consolidated trading history from published analysis ({trades.length} trades)
                </p>
                {source && (
                  <Badge 
                    variant={source.parentBotType === 'shadow' ? 'secondary' : 'default'}
                    className={`
                      ${source.parentBotType === 'shadow' 
                        ? 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-100' 
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-100'
                      }
                    `}
                  >
                    Source: {source.parentBotType === 'shadow' ? 'Shadow' : 'Original'} Parent
                    {source.isFromLinkedBot && ' (via link)'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Enhanced Card */}
      <Card className="overflow-hidden border hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-background/50">
        <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
          {/* Enhanced Controls */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1}-{Math.min(endIndex, sortedTrades.length)} of {sortedTrades.length} trades
            </div>
            <Button 
              variant="outline" 
              size={isMobile ? "sm" : "sm"}
              onClick={onRefresh}
              className={`${isMobile ? "h-9 px-3" : ""} border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200`}
            >
              <RefreshCw className={`${isMobile ? "h-3.5 w-3.5" : "h-4 w-4"} ${isMobile ? "mr-1" : "mr-2"}`} />
              {t("refresh")}
            </Button>
          </div>

          {/* Trading History Table */}
          <div className="overflow-hidden rounded-lg border border-border/50">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Date/Time</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Instrument</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Action</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">P&L</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {paginatedTrades.map((trade, index) => (
                    <tr 
                      key={`${trade.timestamp}-${index}`} 
                      className="hover:bg-muted/20 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-foreground">
                        {(() => {
                          try {
                            // Handle different date formats
                            let date: Date;
                            if (trade.timestamp.includes('T')) {
                              date = new Date(trade.timestamp);
                            } else {
                              // Try parsing YYYY-MM-DD HH:mm:ss format
                              date = new Date(trade.timestamp.replace(' ', 'T') + 'Z');
                            }
                            
                            return !isNaN(date.getTime()) 
                              ? date.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                  hour12: false
                                })
                              : trade.timestamp;
                          } catch (error) {
                            return trade.timestamp;
                          }
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {trade.instrument}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-foreground">
                        {formatAmount(trade.amount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge 
                          variant={trade.action === 'LONG' ? 'default' : 'secondary'}
                          className={`
                            ${trade.action === 'LONG' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-100' 
                              : trade.action === 'SHORT'
                              ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-100'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-900 dark:text-gray-100'
                            }
                          `}
                        >
                          {trade.action}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${
                        trade.normalized_pnl >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(trade.normalized_pnl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-6">
            <TablePagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={sortedTrades.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}